import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

// Phase MVP-FINAL Item 6 — admin-only live security-gate dashboard.
// The backend (services.security_state.snapshot) is the single source
// of truth; this page is a thin renderer over /api/security/status.

const STATUS_META = {
  ok:      { Icon: ShieldCheck,    classes: "bg-emerald-50 text-emerald-700 ring-emerald-200", label: "OK" },
  warn:    { Icon: ShieldAlert,    classes: "bg-amber-50  text-amber-700  ring-amber-200",  label: "Warn" },
  fail:    { Icon: ShieldX,        classes: "bg-rose-50   text-rose-700   ring-rose-200",   label: "Fail" },
  unknown: { Icon: ShieldQuestion, classes: "bg-slate-50  text-slate-700  ring-slate-200",  label: "?" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${meta.classes}`}
      data-testid={`security-gate-status-pill-${status}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </span>
  );
}

export default function AdminSecurityStatusPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "security-status"],
    queryFn: async () => (await api.get("/security/status")).data,
    refetchInterval: 30_000,
  });

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-8" data-testid="admin-security-status-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Security status
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Live snapshot of boot-time security gates.  Read-only — every
            row reflects a value the backend computed at startup or reads
            from the environment.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="security-status-refresh-btn"
        >
          {isFetching ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
          Refresh
        </Button>
      </header>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="security-status-loading">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading security state…
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
             data-testid="security-status-error">
          {formatApiError(error)}
        </div>
      )}

      {data && (
        <>
          <section
            className="rounded-lg border border-slate-200 bg-white p-4"
            data-testid="security-status-overall"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Overall status</div>
                <div className="mt-0.5 text-lg font-semibold text-slate-900" data-testid="security-status-overall-label">
                  {data.overall.toUpperCase()}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-slate-500">Production mode</div>
                <div className="mt-0.5 text-lg font-semibold text-slate-900" data-testid="security-status-is-production">
                  {data.is_production ? "Engaged" : "Dev / preview"}
                </div>
              </div>
            </div>
          </section>

          <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Gate</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Summary</th>
                  <th className="px-4 py-2 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.gates || []).map((g) => (
                  <tr key={g.id} data-testid={`security-gate-row-${g.id}`} className="align-top">
                    <td className="px-4 py-3 font-medium text-slate-800" data-testid={`security-gate-title-${g.id}`}>
                      {g.title}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={g.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-700" data-testid={`security-gate-summary-${g.id}`}>
                      {g.summary}
                    </td>
                    <td className="px-4 py-3 text-xs leading-relaxed text-slate-500" data-testid={`security-gate-detail-${g.id}`}>
                      {g.detail}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}
